import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  STAMP_IMAGE_FIELDS,
  STAMP_SHORT_VALUE_KEYS,
  STAMP_LONG_VALUE_KEYS,
} from '../../../components/client/pass-templates-new/utils/stampCardConstants';

interface ErrorsState {
  [path: string]: string;
}

const initialState: ErrorsState = {};

export const templateErrorsSlice = createSlice({
  name: 'templateErrors',
  initialState,
  reducers: {
    setError: (
      state,
      action: PayloadAction<{ path: string; message: string }>
    ) => {
      const { path, message } = action.payload;
      state[path] = message;
    },
    clearError: (state, action: PayloadAction<string>) => {
      delete state[action.payload];
    },
    setMultipleErrors: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      return { ...state, ...action.payload };
    },
    clearNamespaceErrors: (state, action: PayloadAction<string>) => {
      const ns = action.payload;
      Object.keys(state).forEach((key) => {
        if (key.startsWith(`${ns}.`)) delete state[key];
      });
    },
    clearFormFieldErrors: (state, action: PayloadAction<number>) => {
      const removedIndex = action.payload;
      const prefix = 'form.formFields.';
      const removedPrefix = `${prefix}${removedIndex}.`;
      Object.keys(state).forEach((key) => {
        if (key.startsWith(removedPrefix)) delete state[key];
      });
      const formFieldErrorRegex = /^form\.formFields\.(\d+)\.(.*)$/;
      const keysToShift = Object.keys(state)
        .filter((key) => {
          const m = key.match(formFieldErrorRegex);
          return m && parseInt(m[1], 10) > removedIndex;
        })
        .sort((a, b) => {
          const mA = a.match(formFieldErrorRegex);
          const mB = b.match(formFieldErrorRegex);
          const iA = mA ? parseInt(mA[1], 10) : 0;
          const iB = mB ? parseInt(mB[1], 10) : 0;
          return iB - iA;
        });
      keysToShift.forEach((key) => {
        const m = key.match(formFieldErrorRegex);
        if (!m) return;
        const i = parseInt(m[1], 10);
        const rest = m[2];
        const newKey = `${prefix}${i - 1}.${rest}`;
        state[newKey] = state[key];
        delete state[key];
      });
    },
    clearStampBannerImageErrors: (state) => {
      const platforms = ['shared', 'apple', 'google'] as const;
      const stampImageFields = STAMP_IMAGE_FIELDS;
      platforms.forEach((platform) => {
        stampImageFields.forEach((field) => {
          delete state[`images.${platform}.${field}`];
        });
      });
    },
    clearStampBannerImageErrorsAboveCount: (
      state,
      action: PayloadAction<number>
    ) => {
      const requiredCount = action.payload;
      const platforms = ['shared', 'apple', 'google'] as const;
      const stampImageFieldsAbove = STAMP_IMAGE_FIELDS.slice(requiredCount);
      platforms.forEach((platform) => {
        stampImageFieldsAbove.forEach((field) => {
          delete state[`images.${platform}.${field}`];
        });
      });
    },
    clearStampAttributeErrors: (state) => {
      Object.keys(state).forEach((path) => {
        if (
          path.startsWith('stampShortValues.') ||
          path.startsWith('stampLongValues.') ||
          path.startsWith('notificationMessages.') ||
          path === 'stampCount' ||
          path === 'stampShortKey' ||
          path === 'stampLongKey' ||
          path === 'stampFeedbackTypes' ||
          /^images\.(shared|apple|google)\.stamp\w+Image$/.test(path)
        ) {
          delete state[path];
        }
      });
    },
    clearStampCountRelatedErrorsAboveCount: (
      state,
      action: PayloadAction<number>
    ) => {
      const requiredCount = action.payload;
      const platforms = ['shared', 'apple', 'google'] as const;
      const stampImageFieldsAbove = STAMP_IMAGE_FIELDS.slice(requiredCount);
      const stampShortValueKeysAbove =
        STAMP_SHORT_VALUE_KEYS.slice(requiredCount);
      const stampLongValueKeysAbove =
        STAMP_LONG_VALUE_KEYS.slice(requiredCount);

      platforms.forEach((platform) => {
        stampImageFieldsAbove.forEach((field) => {
          delete state[`images.${platform}.${field}`];
        });
      });
      stampShortValueKeysAbove.forEach((key) => {
        delete state[`stampShortValues.${key}`];
      });
      stampLongValueKeysAbove.forEach((key) => {
        delete state[`stampLongValues.${key}`];
      });
      for (let i = requiredCount; i <= 12; i++) {
        delete state[`notificationMessages.${i}.label`];
      }
    },
    resetErrors: () => initialState,
  },
});

export const {
  setError,
  clearError,
  setMultipleErrors,
  clearNamespaceErrors,
  clearFormFieldErrors,
  clearStampBannerImageErrors,
  clearStampAttributeErrors,
  clearStampBannerImageErrorsAboveCount,
  clearStampCountRelatedErrorsAboveCount,
  resetErrors,
} = templateErrorsSlice.actions;

export default templateErrorsSlice.reducer;
